import {
  Autocomplete,
  Box,
  CircularProgress,
  type SxProps,
  TextField,
  type Theme,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { type FocusEventHandler, type ReactElement, useState } from 'react';

import { InputTooltip } from '../InputTooltip/InputTooltip';
import type { SelectOption } from '../types';

type Props<OptionValueType> = {
  label?: string;
  name?: string;
  options: SelectOption<OptionValueType>[];
  value: SelectOption<OptionValueType> | null;
  onChange: (selectedOption: SelectOption<OptionValueType> | null) => void;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  /** Fired whenever the user types; use it to drive server-side searches. */
  onInputValueChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  /** Shown when there are no options to pick, e.g. when loading them failed. */
  noOptionsText?: string;
  error?: boolean;
  helperText?: string;
  /** Adding a tip will wrap the input component in a hoverable tooltip */
  tip?: string;
  /** Groups the options by their `group`. */
  grouped?: boolean;
  disablePortal?: boolean;
  /** Use if options are filtered elsewhere, e.g. by the API on the input text. */
  disableClientSideFiltering?: boolean;
  sx?: SxProps<Theme>;
};

export const AutocompleteInput = <OptionValueType,>({
  label,
  name,
  options,
  value,
  onChange,
  onBlur,
  onInputValueChange,
  placeholder,
  disabled,
  readOnly,
  loading,
  noOptionsText,
  error,
  helperText,
  tip,
  grouped,
  disablePortal = true,
  disableClientSideFiltering,
  sx,
}: Props<OptionValueType>): ReactElement => {
  // MUI resets the typed text whenever `value` changes identity, and callers often rebuild their
  // options on every render, so keep handing it the same object while the selected option is unchanged
  const [stableValue, setStableValue] = useState(value);
  if (stableValue?.value !== value?.value || stableValue?.label !== value?.label) {
    setStableValue(value);
  }

  const isLoading = loading === true;
  const filterByLabels = createFilterOptions<SelectOption<OptionValueType>>({
    matchFrom: 'any',
    stringify: (option) => `${option.label}${(option.sublabels ?? []).join('')}`,
  });

  const labelWithTip =
    label === undefined ? null : (
      <Box display='flex' gap='0.25rem' alignItems='center'>
        {label}
        {tip !== undefined ? <InputTooltip tip={tip} /> : null}
      </Box>
    );

  return (
    <Autocomplete
      id={name}
      onBlur={onBlur}
      disabled={disabled}
      readOnly={readOnly}
      loading={isLoading}
      loadingText='Loading...'
      noOptionsText={noOptionsText}
      isOptionEqualToValue={(option, val): boolean => option.value === val.value}
      getOptionDisabled={(option): boolean => option.isDisabled === true}
      value={stableValue}
      onChange={(_, selectedOption): void => onChange(selectedOption)}
      onInputChange={(_, inputText): void => onInputValueChange?.(inputText)}
      autoComplete={false}
      disablePortal={disablePortal}
      fullWidth
      options={options}
      groupBy={grouped === true ? (option): string => option.group ?? '' : undefined}
      filterOptions={
        disableClientSideFiltering === true
          ? (opts): SelectOption<OptionValueType>[] => opts
          : filterByLabels
      }
      sx={{ ...sx, ...(readOnly === true ? { pointerEvents: 'none' } : {}) }}
      renderInput={(params): ReactElement => (
        <TextField
          {...params}
          name={name}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
          label={labelWithTip}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color='inherit' size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(params, option): ReactElement => (
        <Box
          {...params}
          key={String(option.value)}
          component='li'
          display='flex'
          flexDirection='column'
          alignItems='start !important'
          padding='0rem'
          whiteSpace='nowrap'
        >
          <Typography>{option.label}</Typography>
          {(option.sublabels ?? [])
            .filter((sublabel) => sublabel !== '')
            .map((sublabel) => (
              <Typography key={sublabel} variant='body3'>
                {sublabel}
              </Typography>
            ))}
        </Box>
      )}
    />
  );
};
